import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PHONE_COUNTRIES, findPhoneCountry, type PhoneValue } from "@/utils/phone";

interface PhoneInputProps {
  /** Id of the number field; the country picker derives its own from it. */
  id: string;
  value: PhoneValue;
  onChange: (value: PhoneValue) => void;
  /** Already-translated message. Rendered below and marks the field invalid. */
  error?: string;
  autoComplete?: string;
}

/**
 * Country picker (flag + dial code, US first) beside the national number.
 *
 * No `required` attribute on purpose: the browser's own "Please fill out this
 * field" is always in the browser's language, which on a Spanish page is how a
 * Miami shop owner gets an English error. Callers validate with phoneToE164()
 * and pass the message down through `error`.
 */
export function PhoneInput({ id, value, onChange, error, autoComplete = "tel-national" }: PhoneInputProps) {
  const { t } = useTranslation("common");
  const country = findPhoneCountry(value.country);

  return (
    <div>
      <div className="flex gap-2">
        <Select
          value={country.iso}
          onValueChange={(iso) => onChange({ ...value, country: iso })}
        >
          <SelectTrigger
            id={`${id}-country`}
            aria-label={t("phone.country")}
            className="h-11 w-[6.75rem] shrink-0 px-2.5"
          >
            <SelectValue>
              <span className="flex items-center gap-1.5">
                <span aria-hidden="true" className="text-base leading-none">{country.flag}</span>
                <span className="tabular-nums">+{country.dial}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-[60vh]">
            {PHONE_COUNTRIES.map((c) => (
              <SelectItem key={c.iso} value={c.iso} className="py-2.5">
                <span className="flex items-center gap-2">
                  <span aria-hidden="true" className="text-base leading-none">{c.flag}</span>
                  <span>{t(`phone.countries.${c.iso}`)}</span>
                  <span className="tabular-nums text-muted-foreground">+{c.dial}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete={autoComplete}
          className="h-11 flex-1"
          placeholder={country.example}
          value={value.national}
          onChange={(e) => onChange({ ...value, national: e.target.value })}
          error={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
        />
      </div>
      {error && (
        <p id={`${id}-error`} className="mt-1.5 text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
